import { prefersAppleMaps } from './maps.ts';

export type RouteMapProvider = 'google' | 'apple' | 'waze';
export type RouteMapChoice = RouteMapProvider | 'auto';
export interface NavigationPoint {
  lat: number;
  lng: number;
}
export interface NavigationPart {
  /** null means navigation from the device's current location to the route start (Waze). */
  from: number | null;
  to: number;
  url: string;
}
export interface NavigationPlan {
  parts: NavigationPart[];
  legs: NavigationPart[];
  invalidPoints: number[];
  appleLegacy: boolean;
}

export function routeMapProvider(choice: RouteMapChoice, userAgent?: string): RouteMapProvider {
  return choice === 'auto' ? (prefersAppleMaps(userAgent) ? 'apple' : 'google') : choice;
}

/** Unknown devices use the lower mobile-browser limit; iPadOS may identify itself as a Mac. */
export function mobileMapsBrowser(userAgent = '', touchPoints = 0): boolean {
  if (/android|iphone|ipad|ipod|mobile/i.test(userAgent)) return true;
  if (/macintosh/i.test(userAgent) && touchPoints > 1) return true;
  return !/windows|macintosh|x11|linux/i.test(userAgent);
}

/** iOS before 18.4 has no multi-stop Apple Maps links. */
export function legacyApple(userAgent: string): boolean {
  const version = /(?:iphone|ipad|ipod).*?OS (\d+)[_.](\d+)/i.exec(userAgent);
  return !!version && (Number(version[1]) < 18 || (Number(version[1]) === 18 && Number(version[2]) < 4));
}

function validPoint(point: NavigationPoint | null | undefined): point is NavigationPoint {
  return (
    !!point &&
    Number.isFinite(point.lat) &&
    Number.isFinite(point.lng) &&
    Math.abs(point.lat) <= 90 &&
    Math.abs(point.lng) <= 180 &&
    (point.lat !== 0 || point.lng !== 0)
  );
}
const text = (point: NavigationPoint) => `${point.lat},${point.lng}`;

function directions(provider: RouteMapProvider, points: NavigationPoint[], legacy = false): string {
  const first = text(points[0]!);
  const last = text(points.at(-1)!);
  if (provider === 'waze')
    return `https://waze.com/ul?${new URLSearchParams({ ll: last, navigate: 'yes', utm_source: 'blastoclean' })}`;
  if (provider === 'google') {
    const query = new URLSearchParams({
      api: '1',
      origin: first,
      destination: last,
      travelmode: 'driving',
      dir_action: 'navigate',
    });
    if (points.length > 2) query.set('waypoints', points.slice(1, -1).map(text).join('|'));
    return `https://www.google.com/maps/dir/?${query}`;
  }
  if (legacy) return `https://maps.apple.com/?${new URLSearchParams({ saddr: first, daddr: last, dirflg: 'd' })}`;
  const query = new URLSearchParams({ source: first, destination: last, mode: 'driving' });
  points.slice(1, -1).forEach((point) => query.append('waypoint', text(point)));
  return `https://maps.apple.com/directions?${query}`;
}

/**
 * Preserve every point, including repeated start/end coordinates. No provider request or navigation.
 * Google: 3 mobile / 9 desktop waypoints, URLs <= 2048 chars.
 * Apple: unified URLs require iOS 18.4+/macOS 15.4+. Use a conservative 13-waypoint
 * batch (14 destinations); the URL documentation does not specify a numerical cap.
 * Waze: one destination per link, from current GPS; explicitly include the route start.
 */
export function buildRouteNavigation(
  points: Array<NavigationPoint | null | undefined>,
  provider: RouteMapProvider,
  options: { userAgent?: string; touchPoints?: number } = {},
): NavigationPlan {
  const invalidPoints = points.flatMap((point, index) => (validPoint(point) ? [] : [index]));
  const appleLegacy = provider === 'apple' && legacyApple(options.userAgent ?? '');
  if (invalidPoints.length || points.length < 2) return { parts: [], legs: [], invalidPoints, appleLegacy };
  // Every item passed the complete coordinate check above. Never filter a missing stop out of a route.
  const checked = points.map((point) => point!);
  const legs = checked.slice(1).map((_, index) => ({
    from: index,
    to: index + 1,
    url: directions(provider, checked.slice(index, index + 2), provider === 'apple'),
  }));
  if (provider === 'waze')
    return {
      parts: [{ from: null, to: 0, url: directions(provider, [checked[0]!]) }, ...legs],
      legs,
      invalidPoints,
      appleLegacy,
    };
  const waypoints =
    provider === 'google' ? (mobileMapsBrowser(options.userAgent, options.touchPoints) ? 3 : 9) : appleLegacy ? 0 : 13;
  const parts: NavigationPart[] = [];
  let from = 0;
  while (from < checked.length - 1) {
    let to = Math.min(from + waypoints + 1, checked.length - 1);
    let url = directions(provider, checked.slice(from, to + 1), appleLegacy);
    while (url.length > 2048 && to > from + 1) {
      to--;
      url = directions(provider, checked.slice(from, to + 1), appleLegacy);
    }
    parts.push({ from, to, url });
    from = to;
  }
  return { parts, legs, invalidPoints, appleLegacy };
}
